import { AmendableSection } from '../../@types/clinicalRecordAmendment';

export const SECTION_LABELS: Record<string, string> = {
  prescreening: 'Prescreening',
  patientSymptoms: 'History & Symptoms',
  refraction: 'Refraction',
  examination: 'Examination',
  handover: 'Handover',
  anteriorEye: 'Anterior Eye',
  contactLensHistoryAndSymptoms: 'CL History & Symptoms',
  contactLensFitting: 'CL Fitting',
  trails: 'CL Trials',
  contactLensAfterCare: 'CL Aftercare',
  contactLensHandover: 'CL Handover',
  teachScreen: 'Teach Screen',
  diabeticEyeScreening: 'Diabetic Eye Screening',
  mecsCues: 'MECS/CUES',
  glaucomaReferralRefinement: 'Glaucoma Referral Refinement',
};

export function sectionLabel(
  section: AmendableSection | string | null,
): string {
  if (!section) return '';
  return SECTION_LABELS[section] ?? humanize(section);
}

/** `camelCase` / `snake_case` → `Camel case`; acronyms (IOPs, HVID, CD) keep their capitals. */
function humanize(key: string): string {
  const words = key
    .replace(/_/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .trim()
    .split(/\s+/)
    // Rejoin a split plural acronym: "IO Ps" → "IOPs".
    .reduce<string[]>((acc, word) => {
      const prev = acc[acc.length - 1];
      if (prev && /^[A-Z]{2,}$/.test(prev) && /^[A-Z]s$/.test(word)) {
        acc[acc.length - 1] = prev + word;
      } else {
        acc.push(word);
      }
      return acc;
    }, [])
    .map((word, i) => {
      if (/^[A-Z]{2,}s?$/.test(word)) return word;
      const lower = word.toLowerCase();
      return i === 0 ? lower.charAt(0).toUpperCase() + lower.slice(1) : lower;
    });
  return words.join(' ');
}

/** `final.rightSph` → `Final › Right sph`; `eyePressures[1].right` → `Eye pressures #2 › Right`. */
export function fieldLabel(fieldPath: string | null): string {
  if (!fieldPath) return '';
  return fieldPath
    .split('.')
    .map((part) =>
      part.replace(
        /^(.*?)\[(\d+)\]$/,
        (_m, name: string, index: string) =>
          `${humanize(name)} #${Number(index) + 1}`,
      ),
    )
    .map((part) => (part.includes('#') ? part : humanize(part)))
    .join(' › ');
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

export function formatAuditValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '(empty)';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value))
    return value.length ? value.map(formatAuditValue).join(', ') : '(empty)';
  if (typeof value === 'string') {
    if (ISO_DATE.test(value)) {
      const d = new Date(value);
      if (!Number.isNaN(d.getTime())) return d.toLocaleString('en-GB');
    }
    if (value.startsWith('data:image/')) return '(image)';
    return value;
  }
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

const META_KEYS = new Set([
  'id',
  'createdDate',
  'updatedDate',
  'createdAt',
  'updatedAt',
  'createdBy',
  'updatedBy',
  'patientId',
  'patient',
  'clinicalRecordId',
  'clinicalRecord',
  'prescreeningId',
  'prescreening',
  'examinationId',
  'examination',
  'anteriorEyeId',
  'anteriorEye',
  'dispenseId',
  'dispense',
  'handoverCompletedAt',
]);

const isObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

/** Same flattening the backend uses for diffs, for displaying a stored version. */
export function flattenForDisplay(
  value: unknown,
  prefix = '',
  out: [string, unknown][] = [],
): [string, unknown][] {
  if (Array.isArray(value)) {
    if (value.every((item) => !isObject(item) && !Array.isArray(item))) {
      if (value.length && prefix) out.push([prefix, value]);
      return out;
    }
    value.forEach((item, i) => flattenForDisplay(item, `${prefix}[${i}]`, out));
    return out;
  }
  if (isObject(value)) {
    Object.keys(value)
      .sort()
      .forEach((key) => {
        if (!META_KEYS.has(key)) {
          flattenForDisplay(value[key], prefix ? `${prefix}.${key}` : key, out);
        }
      });
    return out;
  }
  if (value !== null && value !== undefined && value !== '' && prefix)
    out.push([prefix, value]);
  return out;
}
